import CryptoJS from 'crypto-js';

/**
 * Encryption utility for API keys using AES-256
 * 
 * IMPORTANT: Set VITE_ENCRYPTION_KEY in your .env file
 * Example: VITE_ENCRYPTION_KEY=your-32-character-secret-key-here
 * 
 * For production, use a strong random key (32+ characters)
 */

const ENCRYPTION_KEY = import.meta.env.VITE_ENCRYPTION_KEY || 'default-dev-key-please-change-in-production';

if (ENCRYPTION_KEY === 'default-dev-key-please-change-in-production') {
    console.warn(
        '[Security Warning] Using default encryption key. ' +
        'Set VITE_ENCRYPTION_KEY in your .env file for production.'
    );
}

/**
 * Encrypts a string using AES-256
 * @param plaintext - The text to encrypt (e.g., API key)
 * @returns Encrypted string in Base64 format
 */
export function encryptAES(plaintext: string): string {
    try {
        const encrypted = CryptoJS.AES.encrypt(plaintext, ENCRYPTION_KEY);
        return encrypted.toString();
    } catch (error) {
        console.error('[Encryption Error]', error);
        throw new Error('Failed to encrypt data');
    }
}

/**
 * Decrypts an AES-256 encrypted string
 * @param ciphertext - The encrypted string (Base64 format)
 * @returns Decrypted plaintext
 */
export function decryptAES(ciphertext: string): string {
    try {
        const decrypted = CryptoJS.AES.decrypt(ciphertext, ENCRYPTION_KEY);
        const plaintext = decrypted.toString(CryptoJS.enc.Utf8);

        if (!plaintext) {
            throw new Error('Decryption failed - invalid key or corrupted data');
        }

        return plaintext;
    } catch (error) {
        console.error('[Decryption Error]', error);
        throw new Error('Failed to decrypt data');
    }
}

/**
 * Generates a random encryption key (for setup/testing)
 * @param length - Key length (default: 32 characters)
 * @returns Random key string
 */
export function generateEncryptionKey(length: number = 32): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()_+-=';
    let key = '';
    for (let i = 0; i < length; i++) {
        key += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return key;
}
